import { Component } from '@angular/core';
import { QueryService } from '@pt/api-client';
import { ActionsSubject, Store } from '@ngrx/store';
import { AbstractWidgetDirective } from '../../abstract-widget.directive';
import { UiWidget, WidgetConfiguration } from '@pt/pt-models';
import { WidgetDataRetentionAndDeletionV0ValueDto } from './models/widget-data-retention-and-deletion-v0-value.dto';
import { WidgetDataRetentionAndDeletionV0Utils } from './widget-data-retention-and-deletion-v0.utils';
import { WidgetDataRetentionAndDeletionV0HeaderDto } from './models/widget-data-retention-and-deletion-v0-header.dto';
import { WidgetDataRetentionAndDeletionV0ResponseDto } from './models/widget-data-retention-and-deletion-v0-response.dto';
import { WidgetDataRetentionAndDeletionV0PreviewValueDto } from './models/widget-data-retention-and-deletion-v0-preview-value.dto';
import { WidgetDataRetentionAndDeletionV0PreviewDto } from './models/widget-data-retention-and-deletion-v0-preview.dto';
import { FormControl, FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { WidgetDialogService } from '../../../services/widget-dialog.service';
import { EMPTY, Observable } from 'rxjs';
import { NgxPermissionsService } from 'ngx-permissions';

@Component({
  selector: 'pt-widget-data-retention-and-deletion-v0',
  template: `
    <div class="position-relative" *ngIf="internalStatus.enabled">
      <pt-widget-disabled
        *ngIf="internalStatus.viewMode === 'LOADING'"
        [showLoader]="internalStatus.viewMode === 'LOADING'"
      >
      </pt-widget-disabled>

      <pt-widget-header
        class="w-100"
        [label]="widget?.label"
        [help]="widget?.help"
        [warning]="warningMessage | async"
        [comment]="comment"
        [unsavedComment]="unsavedComment"
        [showCancelButton]="internalStatus.showCancelButton"
        [showCommentButton]="internalStatus.showCommentButton"
        [showEditButton]="internalStatus.showEditButton"
        [showReloadButton]="internalStatus.showReloadButton"
        [showSaveButton]="internalStatus.showSaveButton"
        [showOptButton]="internalStatus.showOptButton"
        [disabledCancelButton]="internalStatus.disabledCancelButton"
        [disabledCommentButton]="internalStatus.disabledCommentButton"
        [disabledEditButton]="internalStatus.disabledEditButton"
        [disabledOptButton]="internalStatus.disabledOptButton"
        [disabledReloadButton]="internalStatus.disabledReloadButton"
        [disabledSaveButton]="internalStatus.disabledSaveButton"
        [loading]="internalStatus.isLoading"
        (clickSaveWidget)="saveWidget(false)"
        (clickResetWidget)="resetWidget(widget)"
        (clickEditWidget)="dispatchEditState()"
        (clickReloadWidget)="reloadWidget(widget)"
        (clickCreateOpt)="createOptWidget()"
        (clickCommentWidget)="clickCommentWidget()"
      >
      </pt-widget-header>

      <ng-container *ngIf="!internalStatus.showOptButton">
        <form [formGroup]="formGroup" *ngIf="!loadingFormGroup">
          <table
            class="table table-striped table-bordered table-sm "
            [ngClass]="{ 'd-none': internalStatus.viewMode !== 'EDIT' }"
          >
            <pt-widget-data-retention-and-deletion-header-v0
              class="display-contents"
              [dataHeader]="dataHeader"
            >
            </pt-widget-data-retention-and-deletion-header-v0>
            <tbody>
              <pt-widget-data-retention-and-deletion-v0-row
                *ngFor="let row of valueWithCategoryName"
                formControlName="{{ row.category }}"
                [header]="dataHeader"
                [unit]="unit"
                [value]="row"
                class="display-contents"
              >
              </pt-widget-data-retention-and-deletion-v0-row>
            </tbody>
          </table>
        </form>

        <pt-widget-data-retention-and-deletion-v0-preview
          *ngIf="internalStatus.viewMode === 'PREVIEW'"
          [noDataLabel]="noDataLabel"
          [loading]="internalStatus.isLoading"
          [previewData]="previewData"
        >
        </pt-widget-data-retention-and-deletion-v0-preview>

        <pt-widget-error
          *ngIf="internalStatus.viewMode === 'ERROR'"
          [errorMessage]="internalStatus.errorMessage | async"
        >
        </pt-widget-error>

        <pt-widget-footer></pt-widget-footer>
      </ng-container>
    </div>
  `,
})
export class WidgetDataRetentionAndDeletionV0Component extends AbstractWidgetDirective<WidgetDataRetentionAndDeletionV0PreviewDto> {
  dataHeader: WidgetDataRetentionAndDeletionV0HeaderDto;
  dataValue: WidgetDataRetentionAndDeletionV0ResponseDto[];
  valueWithCategoryName: WidgetDataRetentionAndDeletionV0PreviewValueDto[];
  unit: WidgetDataRetentionAndDeletionV0ResponseDto[];
  formGroup: FormGroup;
  loadingFormGroup: boolean;
  warningMessage: Observable<string>;

  constructor(
    public store: Store,
    public actionsSubject: ActionsSubject,
    public translateService: TranslateService,
    public widgetDialogService: WidgetDialogService,
    public service: QueryService,
    public permissionService: NgxPermissionsService
  ) {
    super(
      store,
      actionsSubject,
      translateService,
      widgetDialogService,
      permissionService
    );
  }

  loadWidget(widget: UiWidget): void {
    this.dispatchInitState();

    this.loadingFormGroup = true;
    this.service.resolve(widget?.args?.query).subscribe(
      (queryResponse: unknown[]) => {
        const mapping = widget?.args?.query?.mapping;
        this.dataHeader = widget.args.header;
        this.unit = widget?.args?.data?.timeUnits;
        this.dataValue =
          WidgetDataRetentionAndDeletionV0Utils.queryResponseToDataRetentionAndDelentionValue(
            queryResponse,
            mapping
          );

        this.valueWithCategoryName = widget.value
          ? JSON.parse(JSON.stringify(widget.value))
          : [];
        this.valueWithCategoryName.forEach((row) => {
          const v = this.dataValue.find((r) => r.uuid === row.category);
          if (v) {
            row.categoryName = v.label;
          }
        });

        this.valueWithCategoryName = this.valueWithCategoryName.sort((a, b) =>
          a.category > b.category ? -1 : 1
        );

        this.formGroup = new FormGroup({});
        this.valueWithCategoryName?.forEach((v) => {
          this.formGroup.addControl(v.category, new FormControl(v));
          this.formGroup.controls[v.category].updateValueAndValidity();
        });
        this.formGroup.updateValueAndValidity();

        this.valueController(widget, this.dataValue);

        this.checkValidity();
        this.loadingFormGroup = false;

        setTimeout(() => {
          //il timeout aspetta la renderizzazione del form
          //loadingFormGroup = false
          this.dispatchCompleteState({
            header: this.dataHeader,
            value: this.valueWithCategoryName,
            unit: this.unit,
          } as WidgetDataRetentionAndDeletionV0PreviewDto);
        });
      },
      () => {
        this.dispatchErrorState();
      }
    );
  }

  pruningValue(
    value: WidgetDataRetentionAndDeletionV0ValueDto[]
  ): WidgetDataRetentionAndDeletionV0ValueDto[] {
    const response: WidgetDataRetentionAndDeletionV0ValueDto[] = [];
    value?.forEach((v) => {
      if (v.category) {
        response.push({
          category: v.category,
          retentionPeriod: v.retentionPeriodOther
            ? undefined
            : v.retentionPeriod,
          retentionPeriodUnit: v.retentionPeriodOther
            ? undefined
            : v.retentionPeriodUnit,
          retentionPeriodOther: v.retentionPeriodOther,
          retentionPeriodReason: v.retentionPeriodReason,
          pseudonymization: v.pseudonymization,
          anonymization: v.anonymization,
          deletion: v.deletion,
          note: v.note,
        });
      }
    });
    return response;
  }

  saveWidget(ignoreTrigger?: boolean): void {
    const widget = JSON.parse(JSON.stringify(this.widget)) as UiWidget;
    let value: WidgetDataRetentionAndDeletionV0ValueDto[] = [];
    if (this.formGroup?.controls) {
      Object.keys(this.formGroup?.controls).forEach((controlName) => {
        value.push(this.formGroup.controls[controlName].value);
      });
      value = this.pruningValue(value);
    } else {
      value = this.widget.value;
    }
    widget.value = value;
    widget.comment = this.comment;
    this.checkValidity();
    this.completeSaveWidget(widget, ignoreTrigger);
  }

  configuration(): WidgetConfiguration {
    return { supportAutoSave: false, supportValidation: false };
  }

  private checkValidity() {
    if (this.widget?.opt) {
      this.warningMessage = EMPTY;
      this.isValid$.next(true);
    } else {
      if (this.formGroup) {
        switch (true) {
          case this.dataValue?.length > 0 && !this.formGroup?.valid:
            this.isValid$.next(false);
            this.warningMessage = this.translateService.stream(
              'MESSAGE.MISSING_REASON'
            );
            break;

          case this.hasMissingReason(this.valueWithCategoryName):
            this.isValid$.next(false);
            this.warningMessage = this.translateService.stream(
              'MESSAGE.MISSING_REASON'
            );
            break;

          case !this.hasValidActions(this.valueWithCategoryName):
            this.isValid$.next(false);
            this.warningMessage = this.translateService.stream(
              'MESSAGE.MISSING_ACTION'
            );
            break;

          default:
            this.warningMessage = EMPTY;
            this.isValid$.next(true);
        }
        // this.isValid$.next(
        //   this.dataValue?.length > 0 && this.formGroup?.valid
        //   // && this.validPeriod(this.valueWithCategoryName)
        //   && !this.hasMissingReason(this.valueWithCategoryName)
        //   && this.hasValidActions(this.valueWithCategoryName));
      } else {
        this.isValid$.next(this.widget?.value?.length > 0);
      }
    }
  }

  private hasMissingReason(
    valueWithCategoryName: WidgetDataRetentionAndDeletionV0PreviewValueDto[]
  ): boolean {
    let count = 0;
    if (valueWithCategoryName) {
      valueWithCategoryName.forEach((vw) => {
        if (
          vw.retentionPeriodOther &&
          (vw.retentionPeriodReason === null ||
            vw.retentionPeriodReason === undefined ||
            vw.retentionPeriodReason.length < 1)
        ) {
          count += 1;
        }
      });
    }
    return count !== 0;
  }

  // widgetDataRetentionAndDeletionMissingReasonWarning(valueWithCategoryName: WidgetDataRetentionAndDeletionV0PreviewValueDto[]): Observable<string> {
  //   return this.hasMissingReason(valueWithCategoryName) ? this.translateService.stream('MESSAGE.MISSING_REASON') : of(null);
  // }

  private hasValidActions(
    valueWithCategoryName: WidgetDataRetentionAndDeletionV0PreviewValueDto[]
  ): boolean {
    let count = 0;
    if (valueWithCategoryName) {
      valueWithCategoryName.forEach((vw) => {
        if (vw.pseudonymization || vw.anonymization || vw.deletion) {
          count += 1;
        }
      });
    }
    return count === valueWithCategoryName.length;
  }

  // private validPeriod(valueWithCategoryName: WidgetDataRetentionAndDeletionV0PreviewValueDto[]) {
  //   return true;
  // let count = 0;
  // if (valueWithCategoryName) {
  //   valueWithCategoryName.forEach(vw => {
  //     /**
  //      * se retentionPeriodOther = true
  //      * retentionPeriodReason  required
  //      * Esempio:
  //      "value":[
  //      {
  //            "category":"cat2104140649nfj30",
  //            "retentionPeriodOther":true,
  //            "retentionPeriodReason":"TESTO",
  //            "pseudonymization":true,
  //            "anonymization":true,
  //            "deletion":false,
  //            "note":null
  //         }
  //      ],
  //      *
  //      * se retentionPeriodOther = false
  //      * retentionPeriod        required
  //      * retentionPeriodUnit    required
  //      * Esempio
  //      "value":[
  //      {
  //            "category":"cat2104140649nfj30",
  //            "retentionPeriod":1,
  //            "retentionPeriodUnit":"DAY",
  //            "retentionPeriodOther":false,
  //            "retentionPeriodReason":"",
  //            "pseudonymization":true,
  //            "anonymization":true,
  //            "deletion":false
  //         }
  //      ],
  //      */
  //     if (vw.retentionPeriodOther) {
  //       if (vw.retentionPeriodReason?.length > 0) {
  //         count++;
  //       }
  //     } else {
  //       if (vw.retentionPeriod !== null && vw.retentionPeriod !== undefined && vw.retentionPeriodUnit) {
  //         count++;
  //       }
  //     }
  //   });
  // }
  // return count === valueWithCategoryName.length;
  // }

  private valueController(
    widget: UiWidget,
    data: WidgetDataRetentionAndDeletionV0ResponseDto[]
  ) {
    const noData = !data || data?.length === 0;
    if (noData) {
      this.sendWidgetNotificationInvalidQueryResponseNoData(data);
    }

    const widgetValue: WidgetDataRetentionAndDeletionV0PreviewValueDto[] =
      widget.value ? widget.value : [];
    const validValue = WidgetDataRetentionAndDeletionV0Utils.getValidValue(
      widgetValue,
      data
    );
    if (validValue?.length !== widgetValue?.length) {
      const updatedWidget = JSON.parse(JSON.stringify(widget));
      updatedWidget.value = validValue;
      this.sendWidgetNotificationInvalidQueryResponseInvalidValue(
        updatedWidget
      );
    }
  }
}
